import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { maskMyNumber, normalizeMyNumber } from '../../GeneralProfile/useGeneralProfileFlow';
import {
  emptySpouse,
  fetchTaxDeclarationProgress,
  spouseDeclarationFromApi,
  spouseFromApi,
  upsertTaxDeclarationProgress,
} from '../taxDeclarationApi';
import {
  changeReasonLabelKey,
  isSpouseChangeReasonCode,
} from './spouseChangeReason';
import {
  disabilityLabel,
  formatChangeReasonDisplay,
  formatDobDisplay,
  formatIncomeDisplay,
  formatYesNo,
  ineligibilityMessageKey,
  isChangeReasonOtherStored,
  nonResidentReasonLabel,
} from './spouseSectionDisplay';
import {
  clearedIneligibilityResumeStep,
  evaluateSpouseEligibility,
  mergeEligibilityIntoDeclPatch,
  parseIncomeYen,
  reconcileStoredIneligibility,
} from './spouseEligibility';
import {
  firstMissingBranchStep,
  firstStepForMarried,
  hasHelpText,
  isBranchGateStep,
  isSkippedStep,
  isSpouseSectionComplete,
  nextStepAfter,
  prevStepForUndo,
  reviewStep,
  shouldShowIneligibleInHistory,
  stepHelpKey,
  stepIndex,
  stepQuestionKey,
} from './spouseSectionSteps';
import { isSpouseDataFromFamilyInfo } from '../../family/familyInfoSpouseComplete';
import type {
  DisabilityCategory,
  MaritalStatus,
  NonResidentReason,
  SpouseChangeReasonCode,
  SpouseDeclarationSnapshot,
  SpouseSectionStep,
  SpouseSnapshot,
  YesNo,
} from './spouseSectionTypes';
import { SPOUSE_SECTION_STEP_ORDER } from './spouseSectionTypes';

const ADD_SPOUSE_STEPS: SpouseSectionStep[] = ['addSpouseName', 'addSpouseKana', 'addSpouseDob'];

function inferAddSpouseFlowTaken(
  cursor: string,
  validStep: SpouseSectionStep,
  decl: SpouseDeclarationSnapshot
): boolean {
  if (decl.spouseAddedInSection) return true;
  if (ADD_SPOUSE_STEPS.includes(validStep)) return true;
  if (ADD_SPOUSE_STEPS.includes(cursor as SpouseSectionStep)) return true;
  return false;
}

const KANJI_OR_JP_CHARS_RE = /^[\u3040-\u309F\u30A0-\u30FF\u3400-\u9FFF\uF900-\uFAFF\u3000\sー]+$/u;
const KATAKANA_RE = /^[\u30A0-\u30FF\uFF66-\uFF9D\u3000\sー]+$/u;

function emptySpouseDecl(): SpouseDeclarationSnapshot {
  return {
    employeeEstimatedIncome: '',
    sharedLivelihood: '',
    spouseFamilyWorker: '',
    withholdingEligible: false,
    ineligibilityReason: '',
    sectionOutcome: '',
    spouseAddedInSection: false,
  };
}

function userAnswerForStep(
  step: SpouseSectionStep,
  spouse: SpouseSnapshot,
  decl: SpouseDeclarationSnapshot,
  t: (k: string) => string
): string {
  switch (step) {
    case 'notMarriedDone':
      return t('submitDocuments.dependentDeduction.spouse.notMarriedAck');
    case 'addSpouseName':
    case 'confirmFullName':
      return spouse.fullName.trim() || '—';
    case 'addSpouseKana':
    case 'confirmFullNameKana':
      return spouse.fullNameKana.trim() || '—';
    case 'addSpouseDob':
    case 'confirmDob':
      return formatDobDisplay(spouse.dateOfBirth);
    case 'confirmMyNumber':
      return maskMyNumber(spouse.myNumber);
    case 'sharedLivelihood':
      return formatYesNo(t, decl.sharedLivelihood);
    case 'employeeEstimatedIncome':
      return formatIncomeDisplay(decl.employeeEstimatedIncome);
    case 'spouseFamilyWorker':
      return formatYesNo(t, decl.spouseFamilyWorker);
    case 'spouseEstimatedIncome':
      return formatIncomeDisplay(spouse.estimatedIncome);
    case 'ineligibleNotice':
      return decl.ineligibilityReason
        ? t(ineligibilityMessageKey(decl.ineligibilityReason))
        : '—';
    case 'sameHousehold':
      return formatYesNo(t, spouse.livingInSameHousehold);
    case 'addressIfNot':
      return spouse.ifNotAddress.trim() || '—';
    case 'nonResident':
      return formatYesNo(t, spouse.nonResidentFlag);
    case 'nonResidentReason':
      return nonResidentReasonLabel(t, spouse.nonResidentReason);
    case 'disabilityStatus':
      return disabilityLabel(t, spouse.disabilityCategory);
    case 'changeDuringYear':
      return formatYesNo(t, spouse.changeDuringYear);
    case 'changeDate':
      return formatDobDisplay(spouse.changeDate);
    case 'changeReason':
      if (spouse.changeReason.trim() && isSpouseChangeReasonCode(spouse.changeReason)) {
        return formatChangeReasonDisplay(t, spouse.changeReason);
      }
      return t(changeReasonLabelKey('other'));
    case 'changeReasonOther':
      return formatChangeReasonDisplay(t, spouse.changeReason);
    default:
      return '';
  }
}

export function useSpouseSectionFlow(
  email: string | null | undefined,
  taxYear: number,
  t: (key: string) => string,
  onSectionDoneChange?: (done: boolean) => void,
  onOpenFamilyInfo?: () => void
) {
  const [isLoading, setIsLoading] = useState(true);
  const [saveError, setSaveError] = useState(false);
  const [maritalStatus, setMaritalStatus] = useState<MaritalStatus | ''>('');
  const [step, setStep] = useState<SpouseSectionStep>('sharedLivelihood');
  const [spouse, setSpouse] = useState<SpouseSnapshot>(emptySpouse);
  const [decl, setDecl] = useState<SpouseDeclarationSnapshot>(emptySpouseDecl);
  const [dob, setDob] = useState({ year: 1990, month: 1, day: 1 });
  const [changeDob, setChangeDob] = useState({ year: new Date().getFullYear(), month: 1, day: 1 });
  const [reviewMode, setReviewMode] = useState(false);
  const [confirmOnlyFromFamilyInfo, setConfirmOnlyFromFamilyInfo] = useState(false);
  const [editReturnStep, setEditReturnStep] = useState<'review' | null>(null);
  const [editBranchGate, setEditBranchGate] = useState<SpouseSectionStep | null>(null);
  const editingFromReview = editReturnStep === 'review';

  const [notMarriedCommitted, setNotMarriedCommitted] = useState(false);
  const [addSpouseNameCommitted, setAddSpouseNameCommitted] = useState(false);
  const [addSpouseKanaCommitted, setAddSpouseKanaCommitted] = useState(false);
  const [addSpouseDobCommitted, setAddSpouseDobCommitted] = useState(false);
  const [sharedLivelihoodCommitted, setSharedLivelihoodCommitted] = useState(false);
  const [employeeIncomeCommitted, setEmployeeIncomeCommitted] = useState(false);
  const [familyWorkerCommitted, setFamilyWorkerCommitted] = useState(false);
  const [spouseIncomeCommitted, setSpouseIncomeCommitted] = useState(false);
  const [ineligibleCommitted, setIneligibleCommitted] = useState(false);
  const [confirmNameCommitted, setConfirmNameCommitted] = useState(false);
  const [confirmKanaCommitted, setConfirmKanaCommitted] = useState(false);
  const [confirmMyNumberCommitted, setConfirmMyNumberCommitted] = useState(false);
  const [confirmDobCommitted, setConfirmDobCommitted] = useState(false);
  const [sameHouseholdCommitted, setSameHouseholdCommitted] = useState(false);
  const [addressCommitted, setAddressCommitted] = useState(false);
  const [nonResidentCommitted, setNonResidentCommitted] = useState(false);
  const [nonResidentReasonCommitted, setNonResidentReasonCommitted] = useState(false);
  const [disabilityCommitted, setDisabilityCommitted] = useState(false);
  const [changeDuringYearCommitted, setChangeDuringYearCommitted] = useState(false);
  const [changeDateCommitted, setChangeDateCommitted] = useState(false);
  const [changeReasonCommitted, setChangeReasonCommitted] = useState(false);
  const [changeReasonOtherCommitted, setChangeReasonOtherCommitted] = useState(false);
  const [changeReasonOtherDraft, setChangeReasonOtherDraft] = useState('');
  const [changeReasonPendingOther, setChangeReasonPendingOther] = useState(false);

  const applyCommittedThrough = useCallback(
    (
      target: SpouseSectionStep,
      addSpouseFlowTaken = false,
      snapshot?: { spouse: SpouseSnapshot; decl: SpouseDeclarationSnapshot }
    ) => {
      const sp = snapshot?.spouse ?? spouse;
      const d = snapshot?.decl ?? decl;
      const idx = stepIndex(target);
      setNotMarriedCommitted(idx > stepIndex('notMarriedDone'));
      setAddSpouseNameCommitted(addSpouseFlowTaken && idx > stepIndex('addSpouseName'));
      setAddSpouseKanaCommitted(addSpouseFlowTaken && idx > stepIndex('addSpouseKana'));
      setAddSpouseDobCommitted(addSpouseFlowTaken && idx > stepIndex('addSpouseDob'));
      setSharedLivelihoodCommitted(idx > stepIndex('sharedLivelihood'));
      setEmployeeIncomeCommitted(idx > stepIndex('employeeEstimatedIncome'));
      setFamilyWorkerCommitted(idx > stepIndex('spouseFamilyWorker'));
      setSpouseIncomeCommitted(idx > stepIndex('spouseEstimatedIncome'));
      setIneligibleCommitted(shouldShowIneligibleInHistory(idx >= stepIndex('ineligibleNotice'), d));
      setConfirmNameCommitted(idx > stepIndex('confirmFullName'));
      setConfirmKanaCommitted(idx > stepIndex('confirmFullNameKana'));
      setConfirmMyNumberCommitted(idx > stepIndex('confirmMyNumber'));
      setConfirmDobCommitted(idx > stepIndex('confirmDob'));
      setSameHouseholdCommitted(idx > stepIndex('sameHousehold'));
      setAddressCommitted(
        idx > stepIndex('addressIfNot') && !isSkippedStep('addressIfNot', sp, d)
      );
      setNonResidentCommitted(
        idx > stepIndex('nonResident') && !isSkippedStep('nonResident', sp, d)
      );
      setNonResidentReasonCommitted(
        idx > stepIndex('nonResidentReason') && !isSkippedStep('nonResidentReason', sp, d)
      );
      setDisabilityCommitted(idx > stepIndex('disabilityStatus'));
      setChangeDuringYearCommitted(idx > stepIndex('changeDuringYear'));
      setChangeDateCommitted(
        idx > stepIndex('changeDate') && !isSkippedStep('changeDate', sp, d)
      );
      setChangeReasonCommitted(
        idx > stepIndex('changeReason') && !isSkippedStep('changeReason', sp, d)
      );
      setChangeReasonOtherCommitted(
        idx > stepIndex('changeReasonOther') && !isSkippedStep('changeReasonOther', sp, d)
      );
    },
    [decl, spouse]
  );

  const persist = useCallback(
    async (opts: {
      nextStep?: SpouseSectionStep | null;
      spousePatch?: Partial<SpouseSnapshot>;
      declPatch?: Partial<SpouseDeclarationSnapshot>;
      sectionDone?: boolean;
      saveSpouseFamily?: boolean;
      saveSpouseTax?: boolean;
      withholding?: boolean;
    }) => {
      if (!email?.trim()) return false;
      const nextSpouse = { ...spouse, ...opts.spousePatch };
      const nextDecl = { ...decl, ...opts.declPatch };
      if (opts.spousePatch) setSpouse(nextSpouse);
      if (opts.declPatch) setDecl(nextDecl);

      const declPatch = opts.declPatch;
      const needsSpouseTax =
        Boolean(opts.saveSpouseTax) ||
        Boolean(
          declPatch &&
            ('sharedLivelihood' in declPatch ||
              'spouseFamilyWorker' in declPatch ||
              'withholdingEligible' in declPatch ||
              'sectionOutcome' in declPatch ||
              'ineligibilityReason' in declPatch)
        ) ||
        Boolean(
          opts.spousePatch &&
            ('estimatedIncome' in opts.spousePatch ||
              'changeDuringYear' in opts.spousePatch ||
              'changeDate' in opts.spousePatch ||
              'changeReason' in opts.spousePatch)
        );

      const saved = await upsertTaxDeclarationProgress({
        email: email.trim(),
        taxYear,
        ...(opts.nextStep !== undefined ? { currentStepId: opts.nextStep } : {}),
        ...(opts.sectionDone !== undefined ? { sectionSpouseDone: opts.sectionDone } : {}),
        spouseDeclaration: nextDecl,
        ...(opts.saveSpouseFamily ? { spouseFamilyPatch: nextSpouse } : {}),
        ...(needsSpouseTax
          ? {
              spouseTaxPatch: {
                spouse: nextSpouse,
                decl: nextDecl,
                withholding: opts.withholding ?? nextDecl.withholdingEligible,
              },
            }
          : {}),
      });

      if (!saved) {
        setSaveError(true);
        return false;
      }
      setSaveError(false);
      if (saved.spouse) {
        const loaded = spouseFromApi(saved.spouse);
        setSpouse((prev) => ({ ...loaded, changeDuringYear: prev.changeDuringYear || loaded.changeDuringYear }));
      }
      if (opts.sectionDone === true) onSectionDoneChange?.(true);
      if (opts.sectionDone === false) onSectionDoneChange?.(false);
      return true;
    },
    [decl, email, onSectionDoneChange, spouse, taxYear]
  );

  const onSectionDoneChangeRef = useRef(onSectionDoneChange);
  onSectionDoneChangeRef.current = onSectionDoneChange;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!email?.trim()) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      const data = await fetchTaxDeclarationProgress(email.trim(), taxYear);
      if (cancelled) return;

      const ms =
        (data?.maritalStatus as MaritalStatus) ||
        (data?.profile?.maritalStatus as MaritalStatus) ||
        '';
      setMaritalStatus(ms);

      let loadedSpouse = spouseFromApi(data?.spouse);
      let loadedDecl = spouseDeclarationFromApi(data?.declaration ?? {}, data?.spouse);
      if (
        data?.sectionSpouseDone &&
        loadedDecl.sectionOutcome === 'eligible' &&
        !loadedSpouse.changeDate &&
        !loadedSpouse.changeReason
      ) {
        loadedSpouse = { ...loadedSpouse, changeDuringYear: 'no' };
      }
      setSpouse(loadedSpouse);
      if (loadedSpouse.changeDuringYear) {
        loadedDecl.sectionOutcome = loadedDecl.sectionOutcome || '';
      }

      let sectionSpouseDone = Boolean(data?.sectionSpouseDone);
      const reconcile = reconcileStoredIneligibility(loadedDecl, loadedSpouse);
      loadedDecl = reconcile.decl;

      if (reconcile.action === 'cleared' || reconcile.action === 'sync_reason') {
        const persistStep =
          reconcile.action === 'cleared'
            ? reconcile.resumeStep
            : 'ineligibleNotice';
        const saved = await upsertTaxDeclarationProgress({
          email: email.trim(),
          taxYear,
          currentStepId: persistStep,
          ...(reconcile.action === 'cleared' ? { sectionSpouseDone: false } : {}),
          spouseDeclaration: loadedDecl,
        });
        if (!cancelled && saved) {
          if (reconcile.action === 'cleared') {
            sectionSpouseDone = false;
            onSectionDoneChangeRef.current?.(false);
          }
        }
      }

      setDecl(loadedDecl);

      const m = loadedSpouse.dateOfBirth.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (m) setDob({ year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) });
      const cm = loadedSpouse.changeDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (cm) setChangeDob({ year: Number(cm[1]), month: Number(cm[2]), day: Number(cm[3]) });

      if (ms === 'not_married') {
        if (!data?.sectionSpouseDone) {
          const patch: SpouseDeclarationSnapshot = {
            ...emptySpouseDecl(),
            sectionOutcome: 'not_applicable',
          };
          setDecl(patch);
          setNotMarriedCommitted(true);
          setStep('notMarriedDone');
          const saved = await upsertTaxDeclarationProgress({
            email: email.trim(),
            taxYear,
            currentStepId: 'notMarriedDone',
            sectionSpouseDone: true,
            spouseDeclaration: patch,
          });
          if (!cancelled && saved) {
            setReviewMode(true);
            onSectionDoneChangeRef.current?.(true);
          }
        } else {
          setStep('notMarriedDone');
          applyCommittedThrough('notMarriedDone');
          setNotMarriedCommitted(true);
          setReviewMode(true);
        }
        if (!cancelled) setIsLoading(false);
        return;
      }

      const hasSpouse = Boolean(loadedSpouse.fullName.trim());
      const fromFamilyInfo = isSpouseDataFromFamilyInfo(loadedSpouse, loadedDecl);
      if (fromFamilyInfo) {
        setConfirmOnlyFromFamilyInfo(true);
      }
      const cursor = typeof data?.currentStepId === 'string' ? data.currentStepId.trim() : '';
      const validStep = SPOUSE_SECTION_STEP_ORDER.includes(cursor as SpouseSectionStep)
        ? (cursor as SpouseSectionStep)
        : firstStepForMarried(hasSpouse);
      const addSpouseFlowTaken = inferAddSpouseFlowTaken(cursor, validStep, loadedDecl);

      if (fromFamilyInfo && !sectionSpouseDone) {
        const displayTerminal =
          loadedSpouse.changeDuringYear === 'yes'
            ? isChangeReasonOtherStored(loadedSpouse.changeReason)
              ? 'changeReasonOther'
              : 'changeReason'
            : 'changeDuringYear';
        applyCommittedThrough(displayTerminal, false, {
          spouse: loadedSpouse,
          decl: loadedDecl,
        });
        const reason = evaluateSpouseEligibility(loadedDecl, loadedSpouse);
        if (parseIncomeYen(loadedDecl.employeeEstimatedIncome) > 0) {
          setEmployeeIncomeCommitted(true);
          if (reason) {
            setIneligibleCommitted(true);
            setStep('ineligibleNotice');
            setReviewMode(false);
          } else {
            setReviewMode(true);
            setStep(displayTerminal);
            const ok = await persist({
              nextStep: displayTerminal,
              sectionDone: true,
              declPatch: {
                ...loadedDecl,
                sectionOutcome: 'eligible',
                withholdingEligible: true,
                ineligibilityReason: '',
              },
              saveSpouseTax: true,
              withholding: true,
            });
            if (!cancelled && ok) onSectionDoneChangeRef.current?.(true);
          }
        } else {
          setStep('employeeEstimatedIncome');
          setReviewMode(false);
        }
        if (!cancelled) setIsLoading(false);
        return;
      }

      if (sectionSpouseDone) {
        const terminal =
          loadedDecl.sectionOutcome === 'ineligible' ? 'ineligibleNotice' : 'changeDuringYear';
        setStep(terminal);
        applyCommittedThrough(terminal, addSpouseFlowTaken, {
          spouse: loadedSpouse,
          decl: loadedDecl,
        });
        if (loadedDecl.sectionOutcome === 'eligible' && loadedSpouse.changeDuringYear === 'yes') {
          const changeTerminal = isChangeReasonOtherStored(loadedSpouse.changeReason)
            ? 'changeReasonOther'
            : 'changeReason';
          setStep(changeTerminal);
          applyCommittedThrough(changeTerminal, addSpouseFlowTaken, {
            spouse: loadedSpouse,
            decl: loadedDecl,
          });
          if (isChangeReasonOtherStored(loadedSpouse.changeReason)) {
            setChangeReasonOtherDraft(loadedSpouse.changeReason);
          }
        }
        setReviewMode(true);
      } else if (reconcile.action === 'cleared') {
        setIneligibleCommitted(false);
        setStep(reconcile.resumeStep);
        applyCommittedThrough(reconcile.resumeStep, addSpouseFlowTaken, {
          spouse: loadedSpouse,
          decl: loadedDecl,
        });
        setReviewMode(false);
      } else if (cursor) {
        setStep(validStep);
        applyCommittedThrough(validStep, addSpouseFlowTaken, {
          spouse: loadedSpouse,
          decl: loadedDecl,
        });
      } else {
        const firstStep = firstStepForMarried(hasSpouse);
        setStep(firstStep);
        applyCommittedThrough(firstStep, addSpouseFlowTaken, {
          spouse: loadedSpouse,
          decl: loadedDecl,
        });
      }

      if (!cancelled) setIsLoading(false);
    };
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per email/year; avoid loop via persist/decl/spouse deps
  }, [email, taxYear]);

  const isComplete = useMemo(() => {
    if (maritalStatus === 'not_married') return notMarriedCommitted;
    if (decl.sectionOutcome === 'eligible' && spouse.changeDuringYear === 'yes') {
      return (
        changeReasonOtherCommitted ||
        (changeReasonCommitted &&
          isSpouseChangeReasonCode(spouse.changeReason) &&
          spouse.changeReason !== 'other')
      );
    }
    return isSpouseSectionComplete(step, spouse, decl, maritalStatus);
  }, [
    changeReasonCommitted,
    changeReasonOtherCommitted,
    decl,
    maritalStatus,
    notMarriedCommitted,
    spouse,
    step,
  ]);

  const returnToReview = useCallback(
    async (spouseSnap?: SpouseSnapshot) => {
      const sp = spouseSnap ?? spouse;
      const terminal = reviewStep(maritalStatus, decl, sp, {
        changeReasonIsOther: isChangeReasonOtherStored(sp.changeReason),
      });
      setEditReturnStep(null);
      setEditBranchGate(null);
      setChangeReasonPendingOther(false);
      setReviewMode(true);
      setStep(terminal);
      await persist({ nextStep: terminal, sectionDone: true });
    },
    [decl, maritalStatus, persist, spouse]
  );

  const finishStepOrReturnToReview = useCallback(
    async (params: {
      fromStep: SpouseSectionStep;
      nextForward: SpouseSectionStep | null;
      spousePatch?: Partial<SpouseSnapshot>;
      declPatch?: Partial<SpouseDeclarationSnapshot>;
      opts?: { saveSpouseFamily?: boolean; saveSpouseTax?: boolean; withholding?: boolean };
    }) => {
      const mergedSpouse = { ...spouse, ...params.spousePatch };
      const mergedDecl = { ...decl, ...params.declPatch };
      const changeIsOther =
        isChangeReasonOtherStored(mergedSpouse.changeReason) ||
        (params.fromStep === 'changeReasonOther' && changeReasonOtherDraft.trim().length > 0);

      if (editReturnStep === 'review') {
        const ok = await persist({
          spousePatch: params.spousePatch,
          declPatch: params.declPatch,
          sectionDone: false,
          saveSpouseFamily: params.opts?.saveSpouseFamily,
          saveSpouseTax: params.opts?.saveSpouseTax,
          withholding: params.opts?.withholding,
        });
        if (!ok) return;

        const gate =
          editBranchGate ?? (isBranchGateStep(params.fromStep) ? params.fromStep : null);
        if (gate) {
          if (!editBranchGate) setEditBranchGate(gate);
          const missing = firstMissingBranchStep(gate, mergedSpouse, {
            changeReasonIsOther: changeIsOther,
            changeReasonOtherDraft,
            changeReasonPendingOther,
          });
          if (missing) {
            setStep(missing);
            await persist({ nextStep: missing, sectionDone: false });
            return;
          }
        }

        await returnToReview(mergedSpouse);
        return;
      }

      const sectionDone =
        params.nextForward === null &&
        isSpouseSectionComplete(step, mergedSpouse, mergedDecl, maritalStatus);
      const ok = await persist({
        nextStep: params.nextForward,
        spousePatch: params.spousePatch,
        declPatch: params.declPatch,
        sectionDone: sectionDone || undefined,
        saveSpouseFamily: params.opts?.saveSpouseFamily,
        saveSpouseTax: params.opts?.saveSpouseTax,
        withholding: params.opts?.withholding,
      });
      if (!ok) return;
      if (sectionDone) setReviewMode(true);
      if (params.nextForward) setStep(params.nextForward);
    },
    [
      changeReasonOtherDraft,
      changeReasonPendingOther,
      decl,
      editBranchGate,
      editReturnStep,
      maritalStatus,
      persist,
      returnToReview,
      spouse,
      step,
    ]
  );

  const resumeAfterClearedIneligibility = useCallback(
    async (
      declPatch: Partial<SpouseDeclarationSnapshot>,
      spousePatch?: Partial<SpouseSnapshot>
    ) => {
      const mergedSpouse = { ...spouse, ...spousePatch };
      const resume = clearedIneligibilityResumeStep(mergedSpouse);
      setIneligibleCommitted(false);
      setReviewMode(false);
      setEditReturnStep(null);
      setEditBranchGate(null);
      await persist({
        declPatch,
        spousePatch,
        nextStep: resume,
        sectionDone: false,
        ...(parseIncomeYen(mergedSpouse.estimatedIncome) > 0
          ? { saveSpouseTax: true, withholding: true }
          : {}),
      });
      setStep(resume);
    },
    [persist, spouse]
  );

  const applyGateSave = useCallback(
    async (params: {
      fromStep: SpouseSectionStep;
      nextForward: SpouseSectionStep | null;
      declPatch: Partial<SpouseDeclarationSnapshot>;
      spousePatch?: Partial<SpouseSnapshot>;
      opts?: { saveSpouseFamily?: boolean; saveSpouseTax?: boolean; withholding?: boolean };
    }) => {
      const mergedPatch = mergeEligibilityIntoDeclPatch(
        decl,
        spouse,
        params.declPatch,
        params.spousePatch
      );
      const cleared =
        decl.sectionOutcome === 'ineligible' && mergedPatch.sectionOutcome === 'eligible';
      if (cleared) {
        await resumeAfterClearedIneligibility(mergedPatch, params.spousePatch);
        return;
      }
      if (
        mergedPatch.sectionOutcome === 'ineligible' &&
        mergedPatch.ineligibilityReason &&
        mergedPatch.ineligibilityReason !== decl.ineligibilityReason
      ) {
        setIneligibleCommitted(true);
        setReviewMode(true);
        await persist({
          declPatch: mergedPatch,
          spousePatch: params.spousePatch,
          nextStep: null,
          sectionDone: true,
          saveSpouseFamily: params.opts?.saveSpouseFamily,
          saveSpouseTax: params.opts?.saveSpouseTax,
          withholding: params.opts?.withholding ?? false,
        });
        setStep('ineligibleNotice');
        return;
      }
      await finishStepOrReturnToReview({
        fromStep: params.fromStep,
        nextForward: params.nextForward,
        declPatch: mergedPatch,
        spousePatch: params.spousePatch,
        opts: params.opts,
      });
    },
    [decl, finishStepOrReturnToReview, persist, resumeAfterClearedIneligibility, spouse]
  );

  const setCommittedForStep = useCallback((s: SpouseSectionStep) => {
    switch (s) {
      case 'notMarriedDone':
        setNotMarriedCommitted(true);
        break;
      case 'addSpouseName':
        setAddSpouseNameCommitted(true);
        break;
      case 'addSpouseKana':
        setAddSpouseKanaCommitted(true);
        break;
      case 'addSpouseDob':
        setAddSpouseDobCommitted(true);
        break;
      case 'sharedLivelihood':
        setSharedLivelihoodCommitted(true);
        break;
      case 'employeeEstimatedIncome':
        setEmployeeIncomeCommitted(true);
        break;
      case 'spouseFamilyWorker':
        setFamilyWorkerCommitted(true);
        break;
      case 'spouseEstimatedIncome':
        setSpouseIncomeCommitted(true);
        break;
      case 'ineligibleNotice':
        setIneligibleCommitted(true);
        break;
      case 'confirmFullName':
        setConfirmNameCommitted(true);
        break;
      case 'confirmFullNameKana':
        setConfirmKanaCommitted(true);
        break;
      case 'confirmMyNumber':
        setConfirmMyNumberCommitted(true);
        break;
      case 'confirmDob':
        setConfirmDobCommitted(true);
        break;
      case 'sameHousehold':
        setSameHouseholdCommitted(true);
        break;
      case 'addressIfNot':
        setAddressCommitted(true);
        break;
      case 'nonResident':
        setNonResidentCommitted(true);
        break;
      case 'nonResidentReason':
        setNonResidentReasonCommitted(true);
        break;
      case 'disabilityStatus':
        setDisabilityCommitted(true);
        break;
      case 'changeDuringYear':
        setChangeDuringYearCommitted(true);
        break;
      case 'changeDate':
        setChangeDateCommitted(true);
        break;
      case 'changeReason':
        setChangeReasonCommitted(true);
        break;
      case 'changeReasonOther':
        setChangeReasonOtherCommitted(true);
        break;
      default:
        break;
    }
  }, []);

  const handleSendAddSpouseName = useCallback(async () => {
    if (!spouse.fullName.trim()) return;
    setCommittedForStep('addSpouseName');
    await finishStepOrReturnToReview({
      fromStep: 'addSpouseName',
      nextForward: 'addSpouseKana',
      spousePatch: { fullName: spouse.fullName },
      declPatch: { spouseAddedInSection: true },
      opts: { saveSpouseFamily: true },
    });
  }, [finishStepOrReturnToReview, setCommittedForStep, spouse.fullName]);

  const handleSendAddSpouseKana = useCallback(async () => {
    if (!spouse.fullNameKana.trim()) return;
    setCommittedForStep('addSpouseKana');
    await finishStepOrReturnToReview({
      fromStep: 'addSpouseKana',
      nextForward: 'addSpouseDob',
      spousePatch: { fullNameKana: spouse.fullNameKana },
      opts: { saveSpouseFamily: true },
    });
  }, [finishStepOrReturnToReview, setCommittedForStep, spouse.fullNameKana]);

  const handleSendAddSpouseDob = useCallback(async () => {
    const iso = `${dob.year}-${String(dob.month).padStart(2, '0')}-${String(dob.day).padStart(2, '0')}`;
    setCommittedForStep('addSpouseDob');
    setSpouse((s) => ({ ...s, dateOfBirth: iso }));
    await finishStepOrReturnToReview({
      fromStep: 'addSpouseDob',
      nextForward: 'sharedLivelihood',
      spousePatch: { dateOfBirth: iso },
      opts: { saveSpouseFamily: true },
    });
  }, [dob.day, dob.month, dob.year, finishStepOrReturnToReview, setCommittedForStep]);

  const handleSharedLivelihood = useCallback(
    async (v: YesNo) => {
      setCommittedForStep('sharedLivelihood');
      await applyGateSave({
        fromStep: 'sharedLivelihood',
        nextForward: 'employeeEstimatedIncome',
        declPatch: { sharedLivelihood: v },
      });
    },
    [applyGateSave, setCommittedForStep]
  );

  const handleEmployeeIncome = useCallback(async () => {
    if (parseIncomeYen(decl.employeeEstimatedIncome) <= 0) return;
    setCommittedForStep('employeeEstimatedIncome');

    if (confirmOnlyFromFamilyInfo) {
      const mergedDecl = { ...decl, employeeEstimatedIncome: decl.employeeEstimatedIncome };
      const reason = evaluateSpouseEligibility(mergedDecl, spouse);
      const patch: SpouseDeclarationSnapshot = {
        ...mergedDecl,
        ineligibilityReason: reason ?? '',
        sectionOutcome: reason ? 'ineligible' : 'eligible',
        withholdingEligible: !reason,
      };
      setDecl(patch);
      if (reason) {
        setIneligibleCommitted(true);
        setStep('ineligibleNotice');
        await persist({
          nextStep: 'ineligibleNotice',
          sectionDone: false,
          declPatch: patch,
          withholding: false,
        });
        return;
      }
      const terminal =
        spouse.changeDuringYear === 'yes'
          ? isChangeReasonOtherStored(spouse.changeReason)
            ? 'changeReasonOther'
            : 'changeReason'
          : 'changeDuringYear';
      setReviewMode(true);
      setStep(terminal);
      const ok = await persist({
        nextStep: terminal,
        sectionDone: true,
        declPatch: patch,
        saveSpouseTax: true,
        withholding: true,
      });
      if (ok) onSectionDoneChange?.(true);
      return;
    }

    await applyGateSave({
      fromStep: 'employeeEstimatedIncome',
      nextForward: 'spouseFamilyWorker',
      declPatch: { employeeEstimatedIncome: decl.employeeEstimatedIncome },
    });
  }, [
    applyGateSave,
    confirmOnlyFromFamilyInfo,
    decl,
    onSectionDoneChange,
    persist,
    setCommittedForStep,
    spouse,
  ]);

  const handleFamilyWorker = useCallback(
    async (v: YesNo) => {
      setCommittedForStep('spouseFamilyWorker');
      await applyGateSave({
        fromStep: 'spouseFamilyWorker',
        nextForward: 'spouseEstimatedIncome',
        declPatch: { spouseFamilyWorker: v },
      });
    },
    [applyGateSave, setCommittedForStep]
  );

  const handleSpouseIncome = useCallback(async () => {
    const income = spouse.estimatedIncome;
    setCommittedForStep('spouseEstimatedIncome');
    const reason = evaluateSpouseEligibility(
      { ...decl, spouseFamilyWorker: decl.spouseFamilyWorker },
      { ...spouse, estimatedIncome: income }
    );
    if (reason) {
      const patch: Partial<SpouseDeclarationSnapshot> = {
        ineligibilityReason: reason,
        sectionOutcome: 'ineligible',
        withholdingEligible: false,
      };
      setCommittedForStep('ineligibleNotice');
      setReviewMode(true);
      await persist({
        nextStep: null,
        declPatch: patch,
        spousePatch: { estimatedIncome: income },
        sectionDone: true,
        saveSpouseTax: true,
        withholding: false,
      });
      setStep('ineligibleNotice');
      return;
    }
    const patch: Partial<SpouseDeclarationSnapshot> = {
      sectionOutcome: 'eligible',
      withholdingEligible: true,
      ineligibilityReason: '',
    };
    if (editReturnStep === 'review') {
      await finishStepOrReturnToReview({
        fromStep: 'spouseEstimatedIncome',
        nextForward: 'confirmFullName',
        spousePatch: { estimatedIncome: income },
        declPatch: patch,
        opts: { saveSpouseTax: true, withholding: true },
      });
      return;
    }
    await finishStepOrReturnToReview({
      fromStep: 'spouseEstimatedIncome',
      nextForward: 'confirmFullName',
      spousePatch: { estimatedIncome: income },
      declPatch: patch,
      opts: { saveSpouseTax: true, withholding: true },
    });
  }, [decl, editReturnStep, finishStepOrReturnToReview, persist, setCommittedForStep, spouse]);

  const handleConfirmName = useCallback(async () => {
    if (!spouse.fullName.trim()) return;
    setCommittedForStep('confirmFullName');
    await finishStepOrReturnToReview({
      fromStep: 'confirmFullName',
      nextForward: 'confirmFullNameKana',
      spousePatch: { fullName: spouse.fullName },
      opts: { saveSpouseFamily: true },
    });
  }, [finishStepOrReturnToReview, setCommittedForStep, spouse.fullName]);

  const handleConfirmKana = useCallback(async () => {
    if (!spouse.fullNameKana.trim()) return;
    setCommittedForStep('confirmFullNameKana');
    await finishStepOrReturnToReview({
      fromStep: 'confirmFullNameKana',
      nextForward: 'confirmMyNumber',
      spousePatch: { fullNameKana: spouse.fullNameKana },
      opts: { saveSpouseFamily: true },
    });
  }, [finishStepOrReturnToReview, setCommittedForStep, spouse.fullNameKana]);

  const handleConfirmMyNumber = useCallback(async () => {
    const n = normalizeMyNumber(spouse.myNumber);
    if (n.length !== 12) return;
    setCommittedForStep('confirmMyNumber');
    await finishStepOrReturnToReview({
      fromStep: 'confirmMyNumber',
      nextForward: 'confirmDob',
      spousePatch: { myNumber: n },
      opts: { saveSpouseFamily: true },
    });
  }, [finishStepOrReturnToReview, setCommittedForStep, spouse.myNumber]);

  const handleConfirmDob = useCallback(async () => {
    const iso = `${dob.year}-${String(dob.month).padStart(2, '0')}-${String(dob.day).padStart(2, '0')}`;
    setCommittedForStep('confirmDob');
    await finishStepOrReturnToReview({
      fromStep: 'confirmDob',
      nextForward: 'sameHousehold',
      spousePatch: { dateOfBirth: iso },
      opts: { saveSpouseFamily: true },
    });
  }, [dob.day, dob.month, dob.year, finishStepOrReturnToReview, setCommittedForStep]);

  const handleSameHousehold = useCallback(
    async (v: YesNo) => {
      setCommittedForStep('sameHousehold');
      if (v === 'yes') {
        setAddressCommitted(false);
        setNonResidentCommitted(false);
        setNonResidentReasonCommitted(false);
      }
      const patch: Partial<SpouseSnapshot> = {
        livingInSameHousehold: v,
        ifNotAddress: v === 'yes' ? '' : spouse.ifNotAddress,
        nonResidentFlag: v === 'yes' ? 'no' : spouse.nonResidentFlag,
        nonResidentReason: v === 'yes' ? '' : spouse.nonResidentReason,
      };
      if (editReturnStep === 'review') {
        setEditBranchGate('sameHousehold');
      }
      const next = nextStepAfter('sameHousehold', { ...spouse, ...patch }, decl, {
        hasSpouseRecord: true,
        ineligible: false,
      });
      await finishStepOrReturnToReview({
        fromStep: 'sameHousehold',
        nextForward: next,
        spousePatch: patch,
        opts: { saveSpouseFamily: true },
      });
    },
    [decl, editReturnStep, finishStepOrReturnToReview, setCommittedForStep, spouse]
  );

  const handleAddressIfNot = useCallback(async () => {
    if (!spouse.ifNotAddress.trim()) return;
    setCommittedForStep('addressIfNot');
    await finishStepOrReturnToReview({
      fromStep: 'addressIfNot',
      nextForward: 'nonResident',
      spousePatch: { ifNotAddress: spouse.ifNotAddress },
      opts: { saveSpouseFamily: true },
    });
  }, [finishStepOrReturnToReview, setCommittedForStep, spouse.ifNotAddress]);

  const handleNonResident = useCallback(
    async (v: YesNo) => {
      setCommittedForStep('nonResident');
      if (v === 'no') {
        setNonResidentReasonCommitted(false);
      }
      if (editReturnStep === 'review') {
        setEditBranchGate('nonResident');
      }
      const patch: Partial<SpouseSnapshot> = {
        nonResidentFlag: v,
        nonResidentReason: v === 'no' ? '' : spouse.nonResidentReason,
      };
      const next = nextStepAfter('nonResident', { ...spouse, ...patch }, decl, {
        hasSpouseRecord: true,
        ineligible: false,
      });
      await finishStepOrReturnToReview({
        fromStep: 'nonResident',
        nextForward: next,
        spousePatch: patch,
        opts: { saveSpouseFamily: true },
      });
    },
    [decl, editReturnStep, finishStepOrReturnToReview, setCommittedForStep, spouse]
  );

  const handleNonResidentReason = useCallback(
    async (v: NonResidentReason) => {
      setCommittedForStep('nonResidentReason');
      await finishStepOrReturnToReview({
        fromStep: 'nonResidentReason',
        nextForward: 'disabilityStatus',
        spousePatch: { nonResidentReason: v },
        opts: { saveSpouseFamily: true },
      });
    },
    [finishStepOrReturnToReview, setCommittedForStep]
  );

  const handleDisability = useCallback(
    async (v: DisabilityCategory) => {
      setCommittedForStep('disabilityStatus');
      await finishStepOrReturnToReview({
        fromStep: 'disabilityStatus',
        nextForward: 'changeDuringYear',
        spousePatch: { disabilityCategory: v },
        opts: { saveSpouseFamily: true },
      });
    },
    [finishStepOrReturnToReview, setCommittedForStep]
  );

  const handleChangeDuringYear = useCallback(
    async (v: YesNo) => {
      setCommittedForStep('changeDuringYear');
      const patch: Partial<SpouseSnapshot> = {
        changeDuringYear: v,
        changeDate: v === 'no' ? '' : spouse.changeDate,
        changeReason: v === 'no' ? '' : spouse.changeReason,
      };
      if (v === 'no') {
        setChangeReasonOtherDraft('');
        setChangeReasonPendingOther(false);
        setChangeDateCommitted(false);
        setChangeReasonCommitted(false);
        setChangeReasonOtherCommitted(false);
      }
      if (editReturnStep === 'review') {
        setEditBranchGate('changeDuringYear');
      }
      const next = nextStepAfter('changeDuringYear', { ...spouse, ...patch }, decl, {
        hasSpouseRecord: true,
        ineligible: false,
      });
      await finishStepOrReturnToReview({
        fromStep: 'changeDuringYear',
        nextForward: next,
        spousePatch: patch,
        opts: next === null ? { saveSpouseTax: true, withholding: true } : undefined,
      });
    },
    [decl, editReturnStep, finishStepOrReturnToReview, setCommittedForStep, spouse]
  );

  const handleChangeDate = useCallback(async () => {
    const iso = `${changeDob.year}-${String(changeDob.month).padStart(2, '0')}-${String(changeDob.day).padStart(2, '0')}`;
    setCommittedForStep('changeDate');
    await finishStepOrReturnToReview({
      fromStep: 'changeDate',
      nextForward: 'changeReason',
      spousePatch: { changeDate: iso },
      opts: { saveSpouseTax: true, withholding: true },
    });
  }, [changeDob.day, changeDob.month, changeDob.year, finishStepOrReturnToReview, setCommittedForStep]);

  const handleChangeReasonChoice = useCallback(
    async (code: SpouseChangeReasonCode) => {
      setCommittedForStep('changeReason');
      if (code === 'other') {
        setChangeReasonOtherDraft('');
        setChangeReasonPendingOther(true);
        if (editReturnStep === 'review') {
          setEditBranchGate('changeReason');
        }
        await finishStepOrReturnToReview({
          fromStep: 'changeReason',
          nextForward: 'changeReasonOther',
          spousePatch: { changeReason: '' },
        });
        return;
      }
      setChangeReasonPendingOther(false);
      if (editReturnStep === 'review') {
        await finishStepOrReturnToReview({
          fromStep: 'changeReason',
          nextForward: null,
          spousePatch: { changeReason: code },
          opts: { saveSpouseTax: true, withholding: true },
        });
        return;
      }
      setReviewMode(true);
      const ok = await persist({
        nextStep: null,
        spousePatch: { changeReason: code },
        sectionDone: true,
        saveSpouseTax: true,
        withholding: true,
      });
      if (ok) setSpouse((s) => ({ ...s, changeReason: code }));
    },
    [editReturnStep, finishStepOrReturnToReview, persist, setCommittedForStep]
  );

  const handleChangeReasonOther = useCallback(async () => {
    const text = changeReasonOtherDraft.trim();
    if (!text) return;
    setCommittedForStep('changeReasonOther');
    setChangeReasonPendingOther(false);
    if (editReturnStep === 'review') {
      await finishStepOrReturnToReview({
        fromStep: 'changeReasonOther',
        nextForward: null,
        spousePatch: { changeReason: text },
        opts: { saveSpouseTax: true, withholding: true },
      });
      return;
    }
    setReviewMode(true);
    const ok = await persist({
      nextStep: null,
      spousePatch: { changeReason: text },
      sectionDone: true,
      saveSpouseTax: true,
      withholding: true,
    });
    if (ok) setSpouse((s) => ({ ...s, changeReason: text }));
  }, [changeReasonOtherDraft, editReturnStep, finishStepOrReturnToReview, persist, setCommittedForStep]);

  const beginEditField = useCallback(
    async (fieldStep: SpouseSectionStep) => {
      if (confirmOnlyFromFamilyInfo && fieldStep !== 'employeeEstimatedIncome') {
        onOpenFamilyInfo?.();
        return;
      }
      setEditReturnStep('review');
      setEditBranchGate(isBranchGateStep(fieldStep) ? fieldStep : null);
      setReviewMode(false);
      setStep(fieldStep);
      await persist({ nextStep: fieldStep, sectionDone: false });
    },
    [confirmOnlyFromFamilyInfo, onOpenFamilyInfo, persist]
  );

  const handleUndo = useCallback(async () => {
    const prev = prevStepForUndo(step, spouse, decl);
    if (!prev) return;
    setReviewMode(false);
    setStep(prev);
    applyCommittedThrough(prev, decl.spouseAddedInSection, { spouse, decl });
    await persist({ nextStep: prev, sectionDone: false });
  }, [applyCommittedThrough, decl, persist, spouse, step]);

  const enterReviewFromMenu = useCallback(() => {
    if (!isComplete) return;
    setReviewMode(true);
    const terminal = reviewStep(maritalStatus, decl, spouse, {
      changeReasonIsOther: isChangeReasonOtherStored(spouse.changeReason),
    });
    setStep(terminal);
    applyCommittedThrough(terminal, decl.spouseAddedInSection, { spouse, decl });
    if (isChangeReasonOtherStored(spouse.changeReason)) {
      setChangeReasonOtherDraft(spouse.changeReason);
    }
  }, [applyCommittedThrough, decl, isComplete, maritalStatus, spouse]);

  const validateLegalName = useCallback(
    (v: string) => {
      const trimmed = v.trim();
      if (!trimmed) return t('submitDocuments.profileName.validation.required');
      if (!KANJI_OR_JP_CHARS_RE.test(trimmed)) return t('submitDocuments.profileName.validation.kanji');
      return null;
    },
    [t]
  );

  const validateKana = useCallback(
    (v: string) => {
      const s = v.trim();
      if (!s) return t('submitDocuments.profileName.validation.required');
      if (!KATAKANA_RE.test(s)) return t('submitDocuments.profileName.validation.kana');
      return null;
    },
    [t]
  );

  const validateMyNumber = useCallback(
    (v: string) => {
      if (normalizeMyNumber(v).length !== 12) {
        return t('submitDocuments.profileName.validation.myNumber');
      }
      return null;
    },
    [t]
  );

  const helpTextForStep = useCallback(
    (s: SpouseSectionStep) => {
      if (!hasHelpText(s)) return null;
      const key = stepHelpKey(s);
      const text = t(key);
      return text === key ? null : text;
    },
    [t]
  );

  return {
    t,
    step,
    stepQuestionKey,
    helpTextForStep,
    userAnswerForStep: (s: SpouseSectionStep) => userAnswerForStep(s, spouse, decl, t),
    maritalStatus,
    isLoading,
    saveError,
    isComplete,
    spouse,
    setSpouse,
    decl,
    setDecl,
    dob,
    setDob,
    changeDob,
    setChangeDob,
    notMarriedCommitted,
    addSpouseNameCommitted,
    addSpouseKanaCommitted,
    addSpouseDobCommitted,
    sharedLivelihoodCommitted,
    employeeIncomeCommitted,
    familyWorkerCommitted,
    spouseIncomeCommitted,
    ineligibleCommitted,
    confirmNameCommitted,
    confirmKanaCommitted,
    confirmMyNumberCommitted,
    confirmDobCommitted,
    sameHouseholdCommitted,
    addressCommitted,
    nonResidentCommitted,
    nonResidentReasonCommitted,
    disabilityCommitted,
    changeDuringYearCommitted,
    changeDateCommitted,
    changeReasonCommitted,
    changeReasonOtherCommitted,
    changeReasonOtherDraft,
    setChangeReasonOtherDraft,
    handleSendAddSpouseName,
    handleSendAddSpouseKana,
    handleSendAddSpouseDob,
    handleSharedLivelihood,
    handleEmployeeIncome,
    handleFamilyWorker,
    handleSpouseIncome,
    handleConfirmName,
    handleConfirmKana,
    handleConfirmMyNumber,
    handleConfirmDob,
    handleSameHousehold,
    handleAddressIfNot,
    handleNonResident,
    handleNonResidentReason,
    handleDisability,
    handleChangeDuringYear,
    handleChangeDate,
    handleChangeReasonChoice,
    handleChangeReasonOther,
    handleUndo,
    beginEditField,
    enterReviewFromMenu,
    reviewMode,
    confirmOnlyFromFamilyInfo,
    editingFromReview,
    undoLabel: t('submitDocuments.chat.undo'),
    editLabel: t('submitDocuments.fillFormsChecklist.edit'),
    validateLegalName,
    validateKana,
    validateMyNumber,
    canSendAddSpouseName: spouse.fullName.trim().length > 0,
    canSendAddSpouseKana: spouse.fullNameKana.trim().length > 0,
    canSendEmployeeIncome: parseIncomeYen(decl.employeeEstimatedIncome) > 0,
    canSendSpouseIncome: spouse.estimatedIncome.replace(/\D/g, '').length > 0,
    canSendAddress: spouse.ifNotAddress.trim().length > 0,
    canSendConfirmName: spouse.fullName.trim().length > 0,
    canSendConfirmKana: spouse.fullNameKana.trim().length > 0,
    canSendConfirmMyNumber: normalizeMyNumber(spouse.myNumber).length === 12,
    canSendChangeReasonOther: changeReasonOtherDraft.trim().length > 0,
    showUndoAddSpouseName: step === 'addSpouseKana' && addSpouseNameCommitted,
    showUndoAddSpouseKana: step === 'addSpouseDob' && addSpouseKanaCommitted,
    showUndoAddSpouseDob: step === 'sharedLivelihood' && addSpouseDobCommitted,
    showUndoSharedLivelihood: step === 'employeeEstimatedIncome' && sharedLivelihoodCommitted,
    showUndoEmployeeIncome: step === 'spouseFamilyWorker' && employeeIncomeCommitted,
    showUndoFamilyWorker: step === 'spouseEstimatedIncome' && familyWorkerCommitted,
    showUndoSpouseIncome: (step === 'confirmFullName' || step === 'ineligibleNotice') && spouseIncomeCommitted,
    showUndoConfirmName: step === 'confirmFullNameKana' && confirmNameCommitted,
    showUndoConfirmKana: step === 'confirmMyNumber' && confirmKanaCommitted,
    showUndoConfirmMyNumber: step === 'confirmDob' && confirmMyNumberCommitted,
    showUndoConfirmDob: step === 'sameHousehold' && confirmDobCommitted,
    showUndoSameHousehold: (step === 'addressIfNot' || step === 'disabilityStatus') && sameHouseholdCommitted,
    showUndoAddress: step === 'nonResident' && addressCommitted,
    showUndoNonResident: (step === 'nonResidentReason' || step === 'disabilityStatus') && nonResidentCommitted,
    showUndoNonResidentReason: step === 'disabilityStatus' && nonResidentReasonCommitted,
    showUndoDisability: step === 'changeDuringYear' && disabilityCommitted,
    showUndoChangeDuringYear: (step === 'changeDate' || (step === 'changeDuringYear' && changeDuringYearCommitted)) && changeDuringYearCommitted,
    showUndoChangeDate: step === 'changeReason' && changeDateCommitted,
    showUndoChangeReason: step === 'changeReasonOther' && changeReasonCommitted,
    showUndoChangeReasonOther: false,
  };
}

export type SpouseSectionFlow = ReturnType<typeof useSpouseSectionFlow>;
